






























































import {
  defineComponent,
  ref,
  computed,
  PropType,
  watch,
  onMounted,
  nextTick,
  shallowRef,
  triggerRef,
} from '@vue/composition-api';
import type { ContractListItem, ProductItemType } from '@/api/rights/shop-mall/types';
import { formatContractName } from '@/views_v2/pre-apply-choose/utils';
import { Tabs, TabItem } from '@hb/coffee';
import { queryContractList } from '@/api/rights/shop-mall';
import { CreditMallContractSceneEnum, CreditMallFromEnum } from '@/api/rights/shop-mall/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { getNoramlContractIframeUrl } from '../utils';

export default defineComponent({
  name: 'CreditPaymentAgreement',
  components: {
    Tabs,
    TabItem,
  },
  props: {
    /** 初始协议列表（首次渲染使用） */
    initialContractList: {
      type: Array as PropType<ContractListItem[]>,
      default: () => [],
    },
    /** 机构列表（可选，传入时显示机构选择Tabs） */
    productList: {
      type: Array as PropType<ProductItemType[]>,
      default: () => [],
    },
    /** 授信来源 */
    from: {
      type: String,
      default: CreditMallFromEnum.FINANCE_SCREENING_AGREEMENT,
    },
    /** 协议展示区域高度（单位：vm） */
    contractHeight: {
      type: Number,
      default: 430,
    },
    /** 协议展示区域底部边距（单位：vm） */
    contractMarginBottom: {
      type: Number,
      default: 0,
    },
  },
  setup(
    props: {
      initialContractList: ContractListItem[];
      productList: ProductItemType[];
      from: string;
      contractHeight: number;
      contractMarginBottom: number;
    },
  ) {
    /** 当前选中的机构索引 */
    const productIndex = ref(0);
    /** 当前选中的协议索引 */
    const contractIndex = ref(0);
    /** 内部维护的协议列表 - 使用 shallowRef 避免深度响应式 */
    const contractList = shallowRef<ContractListItem[]>([]);
    /** 加载状态 */
    const loading = ref(false);
    /** 强制更新标记 */
    const updateKey = ref(0);

    const hasMultipleProduct = computed(() => props.productList?.length > 1);

    /** 当前协议URL */
    const currentContractUrl = computed(() => {
      if (contractList.value.length === 0 || !contractList.value[contractIndex.value]) {
        return '';
      }
      const contractItem = contractList.value[contractIndex.value];
      const url = contractItem?.contractUrl || '';
      return getNoramlContractIframeUrl(url, contractItem?.contentType);
    });

    /**
     * 获取分期商城协议（失败重试3次）
     * @param productId 产品ID
     * @param retryCount 当前重试次数
     * @returns Promise<ContractListItem[]>
     */
    const getFqMallContract = async (
      productId: string,
      retryCount = 0,
    ): Promise<ContractListItem[]> => {
      const maxRetries = 3;

      try {
        loading.value = true;
        const result = await queryContractList({
          scene: CreditMallContractSceneEnum.CREDIT,
          from: props.from,
          productId,
        });
        loading.value = false;
        // 使用 JSON 深度克隆，彻底避免响应式问题
        const rawList = result?.contractList || [];
        return JSON.parse(JSON.stringify(rawList));
      } catch (err) {
        const error = err as Error;
        // 如果还有重试次数，继续重试
        if (retryCount < maxRetries - 1) {
          return getFqMallContract(productId, retryCount + 1);
        }
        // 最后一次重试失败才上报错误
        loading.value = false;
        ubtSendError(`getFqMallContract error: ${error.message}`);
        return [];
      }
    };

    /** 切换机构 */
    const changeProductIndex = async (index: number) => {
      productIndex.value = index;
      contractIndex.value = 0;

      if (props.productList.length > 0) {
        const currentProduct = props.productList[index];
        if (currentProduct?.productId) {
          contractList.value = [];
          triggerRef(contractList);

          const newContractList = await getFqMallContract(String(currentProduct.productId));
          if (newContractList && newContractList.length > 0) {
            // 直接赋值新数组
            contractList.value = [...newContractList];
            triggerRef(contractList);
          }

          updateKey.value += 1;

          if (contractIndex.value >= contractList.value.length) {
            contractIndex.value = 0;
          }
        }
      }
    };

    /** 切换协议 */
    const changeContractIndex = (index: number) => {
      // 确保索引在有效范围内
      if (index >= 0 && index < contractList.value.length) {
        contractIndex.value = index;
      }
    };

    /** 将 vm 值转换为 vw 单位 */
    const vmToVw = (px: number) => `${(px / 375) * 100}vw`;

    /** 协议展示区域样式 */
    const contractShowStyle = computed(() => ({
      height: vmToVw(props.contractHeight),
      marginBottom: props.contractMarginBottom ? vmToVw(props.contractMarginBottom) : '0',
    }));

    // 组件挂载时初始化协议列表
    onMounted(async () => {
      if (props.initialContractList.length > 0) {
        const cleanList = JSON.parse(JSON.stringify(props.initialContractList));
        contractList.value = [...cleanList];
        triggerRef(contractList);
      } else if (props.productList.length > 0) {
        const firstProduct = props.productList[0];
        if (firstProduct?.productId) {
          const list = await getFqMallContract(String(firstProduct.productId));
          if (list && list.length > 0) {
            contractList.value = [...list];
            triggerRef(contractList);
          }
        }
      }
      updateKey.value += 1;
    });

    return {
      productIndex,
      contractIndex,
      contractList,
      loading,
      updateKey,
      currentContractUrl,
      changeProductIndex,
      changeContractIndex,
      formatContractName,
      contractShowStyle,
      hasMultipleProduct,
    };
  },
});
